/**
 * User Management API (Direct Fetch)
 * 
 * Provides Super Admin functions for managing users across all schools:
 * - listUsersDirect() - List users with optional filters
 * - getUserDirect() - Get user by ID
 * - updateUserRoleDirect() - Update user role (promote/demote)
 * - deleteUserDirect() - Soft delete user (status='removed')
 * 
 * All functions validate Super Admin role where required.
 * 
 * Requirements: 2.4.1, 2.4.2, 2.4.3, 2.4.4, 2.4.5, 3.4.1, 3.4.2, 3.4.3
 */

import type { User, UserFilters } from '@timetable/types';
import { handleApiCall } from './errors';
import { getAccessToken } from './auth-token'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables');
}


/**
 * List all users with optional filters
 * Super Admin can view users across all schools
 * 
 * @param filters - Optional filters for schoolId, role, status
 * @returns Array of users ordered by name
 * 
 * Requirements: 2.4.1, 2.4.5, 3.4.1
 */
export async function listUsersDirect(
  filters?: UserFilters
): Promise<User[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    const params = new URLSearchParams();
    params.append('select', '*');
    
    // Apply filters if provided
    if (filters?.schoolId) {
      params.append('school_id', `eq.${filters.schoolId}`);
    }
    if (filters?.role) {
      params.append('role', `eq.${filters.role}`);
    }
    if (filters?.status) {
      params.append('status', `eq.${filters.status}`);
    }
    
    // Order by name
    params.append('order', 'name.asc');

    const response = await fetch(`${SUPABASE_URL}/rest/v1/users?${params}`, {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch users');
    }

    return await response.json();
  });
}

/**
 * Get user by ID
 * 
 * @param userId - User UUID
 * @returns User object
 * 
 * Requirements: 2.4.1
 */
export async function getUserDirect(userId: string): Promise<User> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    const params = new URLSearchParams();
    params.append('select', '*');
    params.append('id', `eq.${userId}`);

    const response = await fetch(`${SUPABASE_URL}/rest/v1/users?${params}`, {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/vnd.pgrst.object+json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'User not found');
    }

    return await response.json();
  });
}

/**
 * Update user role (promote/demote)
 * Super Admin only - can change user roles
 * 
 * @param userId - User UUID
 * @param newRole - New role (admin, standard, teacher)
 * @returns Updated user object
 * 
 * Requirements: 2.4.2, 2.4.3, 3.4.2
 */
export async function updateUserRoleDirect(
  userId: string,
  newRole: 'admin' | 'standard' | 'teacher'
): Promise<User> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    const response = await fetch(`${SUPABASE_URL}/rest/v1/users?id=eq.${userId}`, {
      method: 'PATCH',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify({
        role: newRole,
        updated_at: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update user role');
    }

    const data = await response.json();
    return data[0];
  });
}

/**
 * Update user status (approve/reject/activate/deactivate)
 * Super Admin only - can change user status
 * 
 * @param userId - User UUID
 * @param newStatus - New status (approved, rejected, inactive, pending)
 * @returns Updated user object
 * 
 * Requirements: 2.4.2, 3.4.2
 */
export async function updateUserStatusDirect(
  userId: string,
  newStatus: 'approved' | 'rejected' | 'inactive' | 'pending'
): Promise<User> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    const response = await fetch(`${SUPABASE_URL}/rest/v1/users?id=eq.${userId}`, {
      method: 'PATCH',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify({
        status: newStatus,
        updated_at: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update user status');
    }

    const data = await response.json();
    return data[0];
  });
}

/**
 * Delete user (soft delete)
 * Sets user status to 'removed' instead of actually deleting the record
 * This preserves data for audit trail
 * 
 * @param userId - User UUID
 * 
 * Requirements: 2.4.4, 3.4.3, 4.2.4
 */
export async function deleteUserDirect(userId: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    const response = await fetch(`${SUPABASE_URL}/rest/v1/users?id=eq.${userId}`, {
      method: 'PATCH',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        status: 'removed',
        updated_at: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete user');
    }
  });
}

/**
 * Update user profile information (name, email)
 * Users can update their own profile information
 * 
 * @param profileData - Profile data to update (name, email)
 * @returns Updated user object
 */
export async function updateUserProfileDirect(
  profileData: { name: string; email: string }
): Promise<User> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    // Get current user ID from token
    const userResponse = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!userResponse.ok) throw new Error('Failed to get current user');
    const userData = await userResponse.json();

    // Update user profile in the users table
    const response = await fetch(`${SUPABASE_URL}/rest/v1/users?id=eq.${userData.id}`, {
      method: 'PATCH',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify({
        name: profileData.name,
        email: profileData.email,
        updated_at: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update profile');
    }

    const data = await response.json();
    
    // If email changed, also update it in Supabase Auth
    if (profileData.email !== userData.email) {
      const authUpdateResponse = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ email: profileData.email }),
      });

      if (!authUpdateResponse.ok) {
        console.warn('Failed to update email in auth, but profile was updated');
      }
    }

    return data[0];
  });
}
